import { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock, User, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(!searchParams.get("role"));
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: searchParams.get("role") || "customer"
  });
  const referralCode = searchParams.get("ref");

  const getSubheading = () => {
    if (!isLogin) {
      if (formData.role === "vendor") return "Create your vendor showroom";
      return "Join our marketplace";
    }
    const from = location.state?.from?.pathname || "";
    if (from.includes("ai-visualizer")) return "Sign in to start designing your space";
    if (from.includes("ai-chat")) return "Sign in to speak with our design experts";
    if (from.includes("cart") || from.includes("checkout")) return "Sign in to continue shopping";
    if (searchParams.get("role") === "vendor") return "Sign in to manage your showroom";
    return "Sign in to your account";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(formData.email, formData.password);
        toast.success(`Welcome back, ${user.name}!`);
        const redirectTo = location.state?.from?.pathname || (user.role === "admin" || user.role === "vendor" ? "/dashboard" : "/ai-visualizer");
        navigate(redirectTo);
      } else {
        const user = await register(formData.email, formData.password, formData.name, formData.role);
        toast.success(`Welcome, ${user.name}!`);
        if (user.role === "vendor") {
          toast.info("Your vendor account is pending approval.");
        }
        // Apply referral code if present
        if (referralCode) {
          try {
            const token = localStorage.getItem("token");
            await axios.post(
              `${API}/referral/apply`,
              { referral_code: referralCode },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Referral code applied!");
          } catch (err) {
            // Silently fail - referral is bonus
          }
        }
        navigate(user.role === "vendor" ? "/dashboard" : "/ai-visualizer");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-['Cormorant_Garamond'] text-3xl font-semibold text-[#1A1B1A] block mb-12">
            <img src="/logo-transparent.png" alt="ARKY World" className="h-10 w-auto" />
          </Link>

          <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1B1A]">
            {isLogin ? "Welcome Back" : "Create Account"}
          </h1>
          <p className="text-[#5C5D5C] mt-2 font-light">
            {getSubheading()}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            {referralCode && !isLogin && (
              <div className="bg-[#3E6656]/10 border border-[#3E6656] p-3 text-sm text-[#3E6656]">
                You've been referred! Create an account and your referrer will earn 10% of your first purchase.
              </div>
            )}
            {!isLogin && (
              <>
                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8F8E]" size={20} />
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="pl-10 bg-white border-[#EAE6DF] rounded-none h-12"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Account Type</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, role: "customer"})}
                      className={`p-4 border text-center transition-all ${
                        formData.role === "customer" 
                          ? "border-[#20C997] bg-[#20C997]/5" 
                          : "border-[#EAE6DF] hover:border-[#20C997]/50"
                      }`}
                      data-testid="role-customer"
                    >
                      <User size={24} className="mx-auto mb-2 text-[#20C997]" />
                      <span className="text-sm font-medium">Customer</span>
                      <p className="text-xs text-[#5C5D5C] mt-1">Shop products</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, role: "vendor"})}
                      className={`p-4 border text-center transition-all ${
                        formData.role === "vendor" 
                          ? "border-[#20C997] bg-[#20C997]/5" 
                          : "border-[#EAE6DF] hover:border-[#20C997]/50"
                      }`}
                      data-testid="role-vendor"
                    >
                      <Store size={24} className="mx-auto mb-2 text-[#F06570]" />
                      <span className="text-sm font-medium">Vendor</span>
                      <p className="text-xs text-[#5C5D5C] mt-1">Sell products</p>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8F8E]" size={20} />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-10 bg-white border-[#EAE6DF] rounded-none h-12"
                  required
                  data-testid="auth-email"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#1A1B1A] mb-2 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8F8E]" size={20} />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pl-10 pr-10 bg-white border-[#EAE6DF] rounded-none h-12"
                  required
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8F8E] hover:text-[#5C5D5C]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none h-12 text-base font-medium"
              data-testid="auth-submit"
            >
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="text-center mt-6 text-[#5C5D5C]">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#20C997] font-medium ml-2 hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </p>

        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1669387448840-610c588f003d?w=1200&q=80" 
          alt="Interior design"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#20C997]/30"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-['Cormorant_Garamond'] text-4xl font-light">
            Transform Your Space
          </h2>
          <p className="mt-4 text-white/80 font-light">
            Discover curated furniture and decor from premium vendors across the UK.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
